import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import React, { useEffect } from "react";
import { useForm, SubmitHandler, FieldErrors } from "react-hook-form";
import {
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import TruncatedText from "Components/Common/TruncatedText";
import { FormValues, AddUserProps, DrpOption } from "../types/user.types";

export const userAddFormFields = (
  roleDropdownData: DrpOption[],
  userDropdownData: DrpOption[]
): FormFieldConfig[] => [
  {
    name: "userid",
    label: "User",
    type: "select",
    options: userDropdownData,
    placeholder: "Select user",
    required: true,
    rules: { required: "User is required" },
  },
  {
    name: "roleid",
    label: "Role",
    type: "select",
    options: roleDropdownData,
    placeholder: "Select role",
    required: true,
    rules: { required: "Role is required" },
  },
];

const AddUser: React.FC<
  AddUserProps & {
    isOpen: boolean;
    toggle: () => void;
  }
> = ({
  isOpen,
  toggle,
  onSave,
  existingData,
  loading = false,
  roleDropdownData = [],
  userDropdownData = [],
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      userid: null,
      roleid: null,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        userid: existingData?.userid ?? null,
        roleid: existingData?.roleid ?? null,
      });
    } else {
      reset({
        userid: null,
        roleid: null,
      });
    }
  }, [isOpen, existingData, reset]);

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const { userid, roleid } = values;

    if (!userid || roleid === null) return;

    onSave({
      userid,
      roleid,
    });
  };

  const handleToggle = () => {
    reset();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      size="md"
      backdrop="static"
      centered
      className="common_modal"
    >
      <ModalHeader toggle={handleToggle} className="fw-bold border-bottom p-3">
        {existingData?.username ? (
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Add User
            <TruncatedText text={`(${existingData.username})`} maxWidth="300px" />
          </span>
        ) : (
          "Add User"
        )}
      </ModalHeader>

      <ModalBody>
        <form id="addUserForm" onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            columns={1}
            control={control}
            errors={errors as FieldErrors}
            fields={userAddFormFields(
              roleDropdownData,
              userDropdownData
            )}
            setValue={setValue}
            getValues={getValues}
          />
        </form>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={handleToggle}>
          Cancel
        </Button>

        <Button
          color="primary"
          outline
          type="submit"
          form="addUserForm"
          disabled={loading || isSubmitting}
        >
          {(loading || isSubmitting) && (
            <Spinner size="sm" className="me-2" />
          )}
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddUser;
